import {useCallback, useRef} from 'react'
import type {ConversationCommentBoxHandle} from '@github-ui/diff-lines/conversation-comment-box'

/** Manage autofocus and return focus for editor. */
export function useEditorFocus<
  ReturnFocusElement extends HTMLElement = HTMLDivElement,
  EditorContainerElement extends HTMLElement = HTMLDivElement,
>({autofocus}: {autofocus: boolean}) {
  const returnFocusRef = useRef<ReturnFocusElement>(null)

  const editorRef = useCallback(
    // This shouldn't ever be null, but sometimes it is at runtime. Possibly a bug in MarkdownEditor
    (editor: ConversationCommentBoxHandle | null) => {
      if (autofocus) editor?.focus()
    },
    [autofocus],
  )

  // Separate container ref is required to keep track of buttons and related editor controls in addition to the editor
  const editorContainerRef = useCallback((container: EditorContainerElement) => {
    return () => {
      if (container?.contains(document.activeElement)) setTimeout(() => returnFocusRef.current?.focus())
    }
  }, [])

  return {
    /** Attach to the element to return focus to when the editor unmounts. */
    returnFocusRef,
    /** Attach to the container around the Markdown editor and controls.  */
    editorContainerRef,
    /** Attach to the Markdown editor itself. */
    editorRef,
  }
}
